import heroAvif768 from '../assets/optimized/hero-768.avif';
import heroAvif1280 from '../assets/optimized/hero-1280.avif';
import heroAvif1920 from '../assets/optimized/hero-1920.avif';
import hero768 from '../assets/optimized/hero-768.webp';
import hero1280 from '../assets/optimized/hero-1280.webp';
import hero1920 from '../assets/optimized/hero-1920.webp';
import { CalendarIcon, StarIcon, ShieldCheckIcon, ExclamationTriangleIcon, ClockIcon, SunIcon, FireIcon, WrenchScrewdriverIcon, XMarkIcon, CurrencyDollarIcon, TrophyIcon, HeartIcon } from '@heroicons/react/24/solid';
import HeatPumpIcon from './icons/HeatPumpIcon';
import MiniSplitIcon from './icons/MiniSplitIcon';
import { useMemo, useState } from 'react';
import eagleLogo from '../assets/optimized/2best-neighbor-home-services-06.webp';
import veteranBadge from '../assets/optimized/veteranbadge.webp';
import { useTypewriter } from '../hooks/useTypewriter';
import { useContent } from '../context/ContentContext';

const SERVICE_ICONS = [SunIcon, FireIcon, HeatPumpIcon, MiniSplitIcon, WrenchScrewdriverIcon, ExclamationTriangleIcon];
const SERVICE_ICON_CLASSES = [
  'w-5 h-5 xl:w-5 xl:h-5 2xl:w-7 2xl:h-7',
  'w-5 h-5 xl:w-5 xl:h-5 2xl:w-7 2xl:h-7',
  'w-7 h-7 xl:w-7 xl:h-7 2xl:w-9 2xl:h-9',
  'w-7 h-7 xl:w-7 xl:h-7 2xl:w-9 2xl:h-9',
  'w-5 h-5 xl:w-5 xl:h-5 2xl:w-7 2xl:h-7',
  'w-5 h-5 xl:w-5 xl:h-5 2xl:w-7 2xl:h-7',
];

const WHY_CHOOSE_ICONS = [ShieldCheckIcon, ClockIcon, CurrencyDollarIcon, TrophyIcon, WrenchScrewdriverIcon, HeartIcon];

export default function Hero() {
  const { content, settings } = useContent();
  const hero = content.hero;
  const locations = useMemo(() => hero.locations, [hero.locations]);
  const locationText = useTypewriter(locations);
  const [showWhyChooseUs, setShowWhyChooseUs] = useState(false);

  return (
    <section className="relative min-h-[80dvh] flex flex-col pb-0 overflow-hidden bg-brand-navy">
      <picture>
        <source type="image/avif" srcSet={`${heroAvif768} 768w, ${heroAvif1280} 1280w, ${heroAvif1920} 1920w`} sizes="100vw" />
      <img
        src={hero1280}
        srcSet={`${hero768} 768w, ${hero1280} 1280w, ${hero1920} 1920w`}
        sizes="100vw"
        width={1920}
        height={1086}
        alt=""
        fetchPriority="high"
        loading="eager"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover object-[center_20%]"
      />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/90 via-brand-navy/70 to-brand-navy/40" />

      <div className="relative z-10 flex-1 flex items-center max-w-site mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 sm:py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center w-full">
          <div>
            <div className="flex items-end gap-3 mb-5 sm:mb-8">
              <img decoding="async" width={200} height={208}
                src={veteranBadge}
                alt="Veteran Owned Business"
                className="w-[72px] h-auto flex-shrink-0 lg:hidden"
              />
              <a
                href="https://share.google/Nef6SHjyajKY3Y14C"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-3.5 py-1.5 sm:px-5 sm:py-2.5 border border-white/10 hover:bg-white/20 transition-colors"
              >
                <div className="flex" style={{ gap: '1px' }}>
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="w-3 h-3 sm:w-4 sm:h-4 text-brand-orange" />
                  ))}
                </div>
                <div className="w-px h-3 bg-white/30" />
                <span className="text-white text-xs sm:text-sm font-medium">{hero.ratingText}</span>
              </a>
            </div>

            <h1 className="text-[34px] leading-[1.1] sm:text-4xl md:text-5xl lg:text-5xl xl:text-[44px] 2xl:text-6xl font-serif font-bold text-white mb-4 sm:mb-6">
              Your Trusted{' '}
              <span className="text-brand-orange">Home Service Partner</span>
              {' '}in
              <span className="block mt-1 min-h-[1.3em] whitespace-nowrap text-[clamp(24px,8vw,34px)] sm:text-inherit">
                {locationText || '\u00A0'}
                <span className="inline-block w-[3px] h-[0.85em] bg-brand-orange ml-1 align-middle animate-blink" />
              </span>
            </h1>

            <p className="text-[15px] sm:text-[17px] lg:text-[19px] xl:text-[15px] 2xl:text-[19px] text-white/80 mb-6 sm:mb-10 xl:mb-8 2xl:mb-10 leading-relaxed max-w-xl">
              {hero.description}
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 lg:gap-5">
              <a
                href={settings.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2.5 bg-brand-orange text-white px-5 py-3.5 sm:px-6 sm:py-4 lg:px-5 lg:py-3.5 xl:px-5 xl:py-3 2xl:px-6 2xl:py-4 rounded-lg font-semibold text-[15px] sm:text-lg lg:text-base xl:text-sm 2xl:text-lg hover:bg-white hover:text-brand-navy transition-all duration-300 shadow-lg shadow-brand-orange/25 w-full sm:w-auto whitespace-nowrap"
              >
                <CalendarIcon className="w-5 h-5" />
                <span className="xl:hidden">Book a Call</span>
                <span className="hidden xl:inline">{hero.bookButtonLabel}</span>
                <span className="w-6 h-6 rounded-full bg-white/20 group-hover:bg-brand-orange/20 flex items-center justify-center transition-colors">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </a>

              <button
                onClick={() => setShowWhyChooseUs(true)}
                className="lg:hidden inline-flex items-center justify-center gap-2.5 bg-white/10 backdrop-blur-md text-white px-5 py-3.5 rounded-lg font-semibold text-[15px] border border-white/20 hover:bg-white/20 transition-all duration-300 w-full sm:w-auto"
              >
                <ShieldCheckIcon className="w-5 h-5 text-brand-orange" />
                <span>{hero.whyChooseUsButtonLabel}</span>
              </button>

              <div className="hidden lg:flex items-center gap-3">
                <img decoding="async" width={200} height={208}
                  src={veteranBadge}
                  alt="Veteran Owned Business"
                  className="w-16 xl:w-16 2xl:w-20 h-auto"
                />
                <div className="text-white/80">
                  <p className="font-semibold text-white text-sm xl:text-sm 2xl:text-base whitespace-nowrap">{hero.veteranBadgeText}</p>
                  <p className="text-xs sm:text-sm hidden 2xl:block">{hero.veteranTagline}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-brand-orange/20 to-brand-tan/20 rounded-3xl blur-2xl" />
              <img decoding="async" width={498} height={401}
                src={eagleLogo}
                loading="lazy"
                alt="Best Neighbor Home Services Eagle"
                className="absolute -top-10 -right-16 w-[160px] 2xl:w-[216px] h-auto z-20"
              />
              <div className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 lg:p-5 xl:p-5 2xl:p-8 lg:space-y-4 xl:space-y-3 2xl:space-y-6">
                <div className="text-center lg:mb-4 xl:mb-3 2xl:mb-8">
                  <span className="text-brand-orange text-xs xl:text-xs 2xl:text-sm font-semibold uppercase tracking-wider">{hero.whyChooseUsButtonLabel}</span>
                </div>
                <div className="flex items-start lg:gap-3 xl:gap-3 2xl:gap-4 lg:p-3 xl:p-2.5 2xl:p-4 bg-white/5 rounded-2xl hover:bg-white/10 transition-colors">
                  <div className="lg:w-11 lg:h-11 xl:w-10 xl:h-10 2xl:w-14 2xl:h-14 bg-brand-orange rounded-xl flex items-center justify-center flex-shrink-0">
                    <ShieldCheckIcon className="lg:w-5 lg:h-5 xl:w-5 xl:h-5 2xl:w-7 2xl:h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold lg:text-base xl:text-sm 2xl:text-lg">Veteran-Owned</p>
                    <p className="text-white/60 text-xs 2xl:text-sm">Military discipline and integrity in every job</p>
                  </div>
                </div>
                <div className="flex items-start lg:gap-3 xl:gap-3 2xl:gap-4 lg:p-3 xl:p-2.5 2xl:p-4 bg-white/5 rounded-2xl hover:bg-white/10 transition-colors">
                  <div className="lg:w-11 lg:h-11 xl:w-10 xl:h-10 2xl:w-14 2xl:h-14 bg-brand-tan rounded-xl flex items-center justify-center flex-shrink-0">
                    <ClockIcon className="lg:w-5 lg:h-5 xl:w-5 xl:h-5 2xl:w-7 2xl:h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold lg:text-base xl:text-sm 2xl:text-lg">24/7 Emergency</p>
                    <p className="text-white/60 text-xs 2xl:text-sm">We are here when you need us most</p>
                  </div>
                </div>
                <div className="flex items-start lg:gap-3 xl:gap-3 2xl:gap-4 lg:p-3 xl:p-2.5 2xl:p-4 bg-white/5 rounded-2xl hover:bg-white/10 transition-colors">
                  <div className="lg:w-11 lg:h-11 xl:w-10 xl:h-10 2xl:w-14 2xl:h-14 bg-brand-orange rounded-xl flex items-center justify-center flex-shrink-0">
                    <ExclamationTriangleIcon className="lg:w-5 lg:h-5 xl:w-5 xl:h-5 2xl:w-7 2xl:h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold lg:text-base xl:text-sm 2xl:text-lg">Smart Technology</p>
                    <p className="text-white/60 text-xs 2xl:text-sm">Advanced smart diagnostics</p>
                  </div>
                </div>
                <div className="pt-3 2xl:pt-4 border-t border-white/10">
                  <div className="flex items-center justify-center lg:gap-4 xl:gap-3 2xl:gap-5">
                    <div className="text-center">
                      <p className="lg:text-2xl xl:text-xl 2xl:text-3xl font-bold text-brand-orange">{hero.stats.jobsCompleted}</p>
                      <p className="text-white/60 text-xs">Jobs Completed</p>
                    </div>
                    <div className="w-px h-8 2xl:h-10 bg-white/20" />
                    <div className="text-center">
                      <p className="lg:text-2xl xl:text-xl 2xl:text-3xl font-bold text-brand-orange">{hero.stats.satisfaction}</p>
                      <p className="text-white/60 text-xs">Satisfaction</p>
                    </div>
                    <div className="w-px h-8 2xl:h-10 bg-white/20 hidden 2xl:block" />
                    <div className="text-center hidden 2xl:block">
                      <p className="text-3xl font-bold text-brand-orange">{hero.stats.citiesServed}</p>
                      <p className="text-white/60 text-sm">Cities Served</p>
                    </div>
                    <div className="w-px h-8 2xl:h-10 bg-white/20" />
                    <div className="text-center">
                      <p className="lg:text-2xl xl:text-xl 2xl:text-3xl font-bold text-brand-orange">{hero.stats.googleRating}</p>
                      <p className="text-white/60 text-xs">Google Rating</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-20 mt-auto hidden md:block">
        <div className="bg-brand-orange">
          <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
            <div className="hidden md:grid md:grid-cols-3 xl:grid-cols-6 divide-x divide-white/20">
              {hero.serviceItems.map((service, i) => {
                const Icon = SERVICE_ICONS[i] || WrenchScrewdriverIcon;
                const iconClass = SERVICE_ICON_CLASSES[i] || 'w-5 h-5';
                return (
                  <a
                    key={i}
                    href={`#services?service=${i}`}
                    className="flex flex-col items-center gap-2 xl:gap-2 2xl:gap-3 py-4 xl:py-4 2xl:py-6 hover:bg-white/10 transition-colors duration-200"
                  >
                    <Icon className={`${iconClass} text-white`} />
                    <span className="text-white font-semibold text-xs xl:text-xs 2xl:text-sm tracking-wide text-center">{service.shortTitle}</span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {showWhyChooseUs && (
        <div
          className="fixed inset-0 z-[9999] flex items-end justify-center lg:hidden"
          onClick={() => setShowWhyChooseUs(false)}
                aria-label="Close why choose us"
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-h-[85dvh] bg-white rounded-t-2xl overflow-y-auto animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-lg font-serif font-bold text-brand-navy">{hero.whyChooseUsButtonLabel}</h3>
              <button
                onClick={() => setShowWhyChooseUs(false)}
                aria-label="Close why choose us"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-brand-navy" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {hero.whyChooseUsItems.map((item, index) => {
                const Icon = WHY_CHOOSE_ICONS[index] || ShieldCheckIcon;
                return (
                  <div key={index} className="flex items-start gap-3.5 p-3.5 bg-brand-off-white rounded-xl">
                    <div className="w-10 h-10 bg-brand-navy rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-brand-navy text-sm mb-0.5">{item.title}</h3>
                      <p className="text-brand-warm-gray text-xs leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-5 pt-2">
              <a
                href={settings.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowWhyChooseUs(false)}
                aria-label="Close why choose us"
                className="block w-full text-center bg-brand-orange text-white py-3.5 rounded-lg font-semibold text-[15px] hover:bg-brand-navy transition-colors"
              >
                {hero.bookButtonLabel}
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
